import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown } from "lucide-react";
import { useLang } from "../i18n";

const EASE = [0.16, 1, 0.3, 1];

const T = {
  it: {
    label: "La nostra missione",
    title: "Aiutare i bambini del Madagascar, ogni giorno.",
    lead:
      "You4Mada è una ONG italiana che sostiene i bambini e le famiglie dei villaggi del sud del Madagascar. Con l'adozione a distanza puoi aiutare un bambino a crescere garantendogli acqua potabile, istruzione, cibo e cure mediche.",
    body:
      "Molte comunità del Madagascar vivono senza accesso all'acqua pulita e senza scuole. Fare beneficenza con You4Mada significa dare un futuro concreto ai bambini africani: costruiamo pozzi, mense scolastiche, serre comunitarie e sosteniamo l'istruzione. Ogni donazione, anche piccola, si trasforma in un cambiamento reale. Puoi donare una volta, attivare un sostegno mensile o partire come volontario in Madagascar.",
    faqLabel: "Domande frequenti",
    faqTitle: "Tutto quello che vuoi sapere prima di donare",
    faq: [
      {
        q: "Come posso aiutare un bambino a distanza?",
        a: "Con l'adozione a distanza puoi sostenere un bambino del sud del Madagascar a partire da 30€ al mese: garantisci cibo, istruzione e cure mediche. Basta compilare il modulo nella pagina Adozione a distanza e completare la donazione in modo sicuro.",
      },
      {
        q: "Dove opera You4Mada?",
        a: "Operiamo nei villaggi del sud del Madagascar, dove aiutiamo bambini e famiglie in condizioni di povertà, portando acqua potabile, scuole e agricoltura sostenibile.",
      },
      {
        q: "Come vengono usate le mie donazioni?",
        a: "Le donazioni finanziano progetti concreti: pozzi per l'acqua potabile, mense scolastiche, materiale didattico, serre comunitarie e assistenza sanitaria per i bambini. Pubblichiamo aggiornamenti e risultati nella sezione News.",
      },
      {
        q: "La donazione online è sicura?",
        a: "Sì. I pagamenti sono gestiti tramite Stripe con crittografia. Puoi donare con carta di credito, PayPal, Apple Pay o Google Pay in totale sicurezza.",
      },
      {
        q: "Posso fare volontariato con You4Mada?",
        a: "Certo. Puoi partire per il Madagascar, organizzare eventi in Italia o donare le tue competenze. Trovi tutte le informazioni nella pagina Volontariato.",
      },
      {
        q: "Posso interrompere il sostegno quando voglio?",
        a: "Sì, senza vincoli. Puoi interrompere o modificare la tua donazione mensile in qualsiasi momento scrivendoci a you4mada@gmail.com.",
      },
    ],
  },
  en: {
    label: "Our mission",
    title: "Helping the children of Madagascar, every day.",
    lead:
      "You4Mada is an Italian NGO supporting children and families in the villages of southern Madagascar. Through child sponsorship you can help a child grow by providing clean water, education, food and healthcare.",
    body:
      "Many communities in Madagascar live without access to clean water or schools. Giving to charity with You4Mada means offering a real future to African children: we build wells, school canteens and community greenhouses, and we support education. Every donation, however small, becomes real change. You can give once, start a monthly donation, or join us as a volunteer in Madagascar.",
    faqLabel: "Frequently asked questions",
    faqTitle: "Everything you want to know before donating",
    faq: [
      {
        q: "How can I sponsor a child?",
        a: "With child sponsorship you can support a child in southern Madagascar from €30 per month: you provide food, education and healthcare. Just fill in the form on the Child Sponsorship page and complete your donation securely.",
      },
      {
        q: "Where does You4Mada work?",
        a: "We work in the villages of southern Madagascar, helping children and families living in poverty by bringing clean water, schools and sustainable agriculture.",
      },
      {
        q: "How are my donations used?",
        a: "Donations fund concrete projects: water wells, school canteens, learning materials, community greenhouses and healthcare for children. We publish updates and results in the News section.",
      },
      {
        q: "Is online donation safe?",
        a: "Yes. Payments are processed through Stripe with encryption. You can donate by credit card, PayPal, Apple Pay or Google Pay in complete safety.",
      },
      {
        q: "Can I volunteer with You4Mada?",
        a: "Absolutely. You can travel to Madagascar, organize events in Italy, or share your skills. You'll find all the details on the Volunteer page.",
      },
      {
        q: "Can I stop my support whenever I want?",
        a: "Yes, with no strings attached. You can stop or change your monthly donation at any time by writing to you4mada@gmail.com.",
      },
    ],
  },
};

export default function MissioneFaq() {
  const { lang } = useLang();
  const m = T[lang] || T.it;
  const [openFaq, setOpenFaq] = useState(0);

  return (
    <section id="missione" data-testid="missione-faq-section" className="relative border-t border-white/10 py-28 md:py-36">
      <div className="mx-auto max-w-4xl px-5 md:px-8">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, ease: EASE }}
          className="text-[12px] font-semibold uppercase tracking-[0.35em] text-primary"
        >
          {m.label}
        </motion.p>
        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, delay: 0.1, ease: EASE }}
          className="mt-5 font-serif text-4xl leading-[1.05] tracking-tight text-white md:text-5xl"
        >
          {m.title}
        </motion.h2>
        <motion.p
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, delay: 0.2, ease: EASE }}
          className="mt-6 max-w-3xl text-lg leading-relaxed text-white/80"
        >
          {m.lead}
        </motion.p>
        <motion.p
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, delay: 0.28, ease: EASE }}
          className="mt-5 max-w-3xl text-base leading-relaxed text-white/65"
        >
          {m.body}
        </motion.p>

        <div className="mt-20">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, ease: EASE }}
            className="text-[12px] font-semibold uppercase tracking-[0.35em] text-primary"
          >
            {m.faqLabel}
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, delay: 0.1, ease: EASE }}
            className="mt-5 font-serif text-3xl leading-[1.05] tracking-tight text-white md:text-4xl"
          >
            {m.faqTitle}
          </motion.h2>

          <div className="mt-12 border-t border-white/10">
            {m.faq.map((f, i) => (
              <div key={f.q} className="border-b border-white/10">
                <button
                  data-testid={`home-faq-${i}`}
                  onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
                  className="flex w-full items-center justify-between gap-6 py-7 text-left"
                >
                  <span className="font-serif text-xl tracking-tight text-white md:text-2xl">
                    {f.q}
                  </span>
                  <ChevronDown
                    size={20}
                    className={`shrink-0 text-primary transition-transform duration-300 ${
                      openFaq === i ? "rotate-180" : ""
                    }`}
                  />
                </button>
                <AnimatePresence initial={false}>
                  {openFaq === i && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.35, ease: EASE }}
                      className="overflow-hidden"
                    >
                      <p className="max-w-2xl pb-7 text-base leading-relaxed text-white/65">{f.a}</p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
